import {FC, PropsWithChildren, ReactNode, useCallback, useMemo} from 'react';
import {ConstructionComponentData, ConstructionComponentProps} from '@interfaces/constructor/constructor';
import { useTheme } from '@hooks/panelHooks';
import classNames from 'classnames';
import { useAppDispatch } from '@services/hooks/helperHooks';
import { sidebarActions } from '@store/sidebarStore';
import { getComponent } from './getComponent';
import { Theme } from '@interfaces/panel/themes';
import { auth } from '@services/auth';
import { useGlobalVariables } from '@hooks/selectors/panel.selectors';
import { KeyValueSet } from '@interfaces/helpers';
import { panelActions } from '@store/panelStore';
import {Parser} from "expr-eval-fork";
import {getNestedValue} from "@/helpers/utils";

function first(...values: any[]) {
    return values.find(x => x !== undefined);
}

function replaceValue(value: string, childProps: any, globalVars: KeyValueSet<string | number>) {

    //Дополнительная проверка передаваемого типа
    if(typeof value !== 'string') {
        return value;
    }

    if (/^\{[A-Za-z-._]+}$/.test(value)) {
        const c = value.substring(1, value.length - 1);
        return first(getNestedValue(childProps, c), getNestedValue(globalVars, c));
    }

    if (/^!\{[A-Za-z-._]+}$/.test(value)) {
        const c = value.substring(2, value.length - 1);
        const val = first(getNestedValue(childProps, c), getNestedValue(globalVars, c));

        if (val !== undefined) {
            return !val;
        }
        return value;
    }
    const matches = value.match(/\{[A-Za-z-._]+}/g);

    if (!matches) {
        return value;
    }

    let result: string = value;
    matches.forEach(match => {
        const c = match.substring(1, match.length - 1);
        const val = first(getNestedValue(childProps, c), getNestedValue(globalVars, c));

        if (val !== undefined) {
            result = result.replace(match, val.toString());
        }
    });

    // Проверка и вычисление значений для всех вхождений шаблона "calc(somevalue)"
    result = result.replace(/calc\((.+?)\)/g, (match, p1) => {
        try {
            return Parser.evaluate(p1).toString();
        } catch (e) {
            console.error('Error evaluating expression:', e);
            return match;
        }
    });

    return result;
}

function replaceProps(props: Record<string, any>, childProps: any, globalVars: KeyValueSet<string | number>, actions: KeyValueSet<(...args: any[]) => any>) {

    if (typeof props !== 'object' || props === null || Array.isArray(props) || !childProps) {
        return props;
    }

    const newProps = { ...props };

    Object.entries(props).forEach(([key, value]) => {

        if (typeof value === 'string' && value) {

            if(key === 'activeClassName') {
                //{({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
            }

            //Если значение - строка, то заменяем в ней все переменные
            const result = replaceValue(value, childProps, globalVars);
            if(result !== undefined) {
                newProps[key] = result;
            }
        }
        else if(key === 'ref' || value === null || value === undefined || typeof value !== 'object') {
            //Игнорируем объект рефа, пустые значения и значения-не объекты. Их мы заменили ранее
            return;
        }
        else if (Array.isArray(value)) {
            newProps[key] = value.map(x =>
                typeof x === 'object' ? replaceProps(x, childProps, globalVars, actions)
                : replaceValue(x, childProps, globalVars)
            );
        }
        else if(typeof value === 'object') {
            //Если значение = объект - рекурсивно обрабатываем объект с заменой значений

            const p = replaceProps(newProps[key], childProps, globalVars, actions);
            if (p !== newProps[key]) {
                newProps[key] = p;
            }

            //Если значение - объект с полем action - пробуем собрать из него вызов функции
            const v = p as Record<string, any>;

            if(typeof v.action === 'string') {
                const action = actions[v.action] || childProps[v.action];
                if(typeof action !== 'function') {
                    return;
                }
                const params = v.params ? (Array.isArray(v.params) ? v.params : [v]) : [];
                // Array.isArray(v.params) ? v.params.map(x => replaceValue(x, childProps, globalVars)) : [];
                const func = replaceValue(v.function, childProps, globalVars);

                newProps[key] = () => {
                    action(...params);
                    if(typeof func === 'function') {
                        func();
                    }
                };
            }
        }
    });

    if (newProps.className && typeof newProps.className === 'object') {

        Object.entries(newProps.className).forEach(([key, value]) => {
            if(typeof value === "string") {
                try {
                    newProps.className[key] = Boolean(Parser.evaluate(value));
                }
                catch(e) {}
            }
        });

        const newCN = classNames(newProps.className);
        if (newProps.className !== newCN) {
            //console.log(newProps.className);
            newProps.className = newCN;
        }
    }

    if(newProps.activeClassName) {
        const className = newProps.className;
        newProps.className = ({ isActive }: { isActive: boolean }) => {
            return classNames(className, {
                [newProps.activeClassName]: isActive
            })
        };
        delete newProps.activeClassName;
    }

    return newProps;
}



function toArray<T>(values: T | T[] | undefined): T[] {
    if (Array.isArray(values)) {
        return values;
    }
    else if (values) {
        return [values];
    }
    return [];
}

function makeChildrens(
    theme: Theme | undefined, 
    childData: ConstructionComponentData | ConstructionComponentData[] | undefined, 
    childProps: any, 
    children: ReactNode,
    proxiedChildren: ReactNode,
    globalVars: KeyValueSet<string | number>,
    actions: KeyValueSet<() => any>,
    wrap: boolean = false) {

    if (childData) {
        return toArray(childData).map((child, idx) => {

            const info = getComponent(child.component, theme);
            const isRoute = child.component === 'Route' || child.component === 'Routes' || child.component === 'Navigate';

            if ((typeof info === 'string' || isRoute) && (!wrap || isRoute) && info !== 'Children') {

                const Tag = typeof info === 'string' ? info : info.component;
                const newProps = replaceProps(child.props, childProps, globalVars, actions);
                const childrens = makeChildrens(theme, child.children, childProps, children, proxiedChildren, globalVars, actions);

                if(child.component === 'Route') {
                    return <Tag key={child.guid || idx} {...newProps} element={childrens}/>
                }

                return (
                    <Tag key={child.guid || idx} {...newProps}>
                        {childrens}
                    </Tag>
                )
            }

            return (
                <ConstructionComponent key={child.guid || idx} data={child} childProps={childProps} proxiedChildren={children || proxiedChildren} />
            )
        });
    }
    else {
        return null;
    }
}


export const ConstructionComponent: FC<PropsWithChildren<ConstructionComponentProps>> = ({ data, name, childProps, children, proxiedChildren }) => {

    const theme = useTheme();
    const dispatch = useAppDispatch();
    const globalVars = useGlobalVariables();

    const setGlobalVariable = useCallback((name: string, value: string) => dispatch(panelActions.setVariable({
        name, value
    })), [dispatch]);

    const deleteGlobalVariable = useCallback((name: string) => dispatch(panelActions.deleteVariable(name)), [dispatch]); 

    const sidebar_toggle = useCallback(() => dispatch(sidebarActions.toggleSidebar()), [dispatch]);

    const actions: KeyValueSet<(...args: any[]) => any> = useMemo(() => ({
        setGlobalVariable,
        deleteGlobalVariable
    }), [deleteGlobalVariable, setGlobalVariable]);

    //инициализируем глобальные функции в childProps
    if (!childProps)
        childProps = {};
    if (!childProps.sidebar_toggle)
        childProps.sidebar_toggle = sidebar_toggle;
    if (!childProps.logout)
        childProps.logout = auth().signOut;

    if (theme?.components && name) {
        if (name.startsWith('CC.')) {
            name = name.substring(3)
        }
        data = theme.components[name];
        //console.log(`CC: ${JSON.stringify(data)}`);
    }

    let broke = false;

    if (!data) {
        broke = true; //костыль, чтобы вызвать useMemo
        data = {
            component: '',
            children: undefined,
            props: {}
        }
    }

    let { component: componentName, children: childData, props } = data;

    if (!componentName) {
        console.log(data);
    }

    //Заменяем переменные в пропсах
    const newProps = useMemo(() => {
        return replaceProps(props, childProps, globalVars, actions) || {};
    }, [props, childProps, globalVars, actions]);
    //const newProps = replaceProps(props, childProps) || {};

    if (broke)
        return null;

    const ComponentInfo = getComponent(componentName, theme);

    if (!ComponentInfo) {
        console.log(`${componentName} not found`);
    }

    if (typeof ComponentInfo === 'string') {

        if (ComponentInfo === 'Children') {
            return (
                <>{proxiedChildren}</>
            )
        }

        return (
            <ComponentInfo key={data.guid} {...newProps as any}>
                {makeChildrens(theme, childData, childProps, children, proxiedChildren, globalVars, actions)}
            </ComponentInfo>
        )
    }

    const { injectChild, takedChildProps, component: Component } = ComponentInfo;

    if(!Component) {
        console.log(`Отсутствует компонент ${componentName}`);
        console.log(ComponentInfo);
    }

    toArray(injectChild).forEach(c => { //Внедряем нужные пропсы

        if (childProps[c] !== undefined) {
            newProps[c] = childProps[c];
        }

    });

    toArray(takedChildProps).forEach(p => { //Если некоторые пропсы не нужно передавать дальше
        delete childProps[p]; //удаляем пропс
    });

    if (componentName.startsWith('CC.')) {
        if(data.props) {
            childProps = { ...childProps, ...data.props };
        }

        return (
            <ConstructionComponent name={componentName} childProps={childProps} proxiedChildren={children} >
                {makeChildrens(theme, childData, childProps, children, proxiedChildren, globalVars, actions)}
            </ConstructionComponent>
        )
    }

    return (
        <Component key={data.guid} {...newProps}>
            {makeChildrens(theme, childData, childProps, children, proxiedChildren, globalVars, actions, true)}
        </Component>
    )
}

export default ConstructionComponent;